import { useState } from 'react'
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { safeAppRedirectPath, redirectPathFromLoginState } from '@/lib/auth-redirect'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Loader2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { BRAND } from '@/lib/config/brand'
import { testIds } from '@/lib/test-ids'
import { AuthLayout } from './auth-layout'
import { AuthFormAlert } from './auth-form-alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PasswordInput } from './password-input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'

const loginSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
})

type LoginFormValues = z.infer<typeof loginSchema>

export function LoginForm() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const message = searchParams.get('message')
  const redirectAfterAuth =
    safeAppRedirectPath(searchParams.get('redirect')) ??
    redirectPathFromLoginState(location.state)
  const [generalError, setGeneralError] = useState<string | null>(null)

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  })

  const onSubmit = async (values: LoginFormValues) => {
    if (!supabase) return
    setGeneralError(null)
    const { error } = await supabase.auth.signInWithPassword({
      email: values.email,
      password: values.password,
    })
    if (error) {
      setGeneralError(error.message === 'Invalid login credentials' ? 'Invalid email or password.' : error.message)
      return
    }
    // Do not call updateUser() here. It was stamping password_set_at on every
    // login and racing GoTrue's initialize()/getSession() lock (incident:
    // 2026-09-01-supabase-navigator-locks-abort-cascade). Account settings and
    // reset-password still set that metadata when the password actually changes.
    navigate(redirectAfterAuth ?? '/', { replace: true })
  }

  return (
    <AuthLayout title={`Log in to ${BRAND.displayName}`}>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-4"
          data-testid={testIds.auth.loginForm}
        >
          {message && <AuthFormAlert message={message} variant="success" />}
          {generalError && <AuthFormAlert message={generalError} />}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    autoComplete="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <PasswordInput placeholder="" autoComplete="current-password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            className="w-full"
            disabled={form.formState.isSubmitting}
          >
            {form.formState.isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Log in'
            )}
          </Button>
          <div className="space-y-1 text-center text-sm">
            <p className="text-xs text-muted-foreground">
              First time? Open the link in your invite email (it lasts 1 hour).
              If it expired, that page can send a new invite.
            </p>
            <p>
              <Link
                to="/forgot-password"
                className="text-primary underline-offset-4 hover:underline"
              >
                Forgot password?
              </Link>
            </p>
            <p className="text-muted-foreground">
              Don&apos;t have an account?{' '}
              <Link
                to={
                  redirectAfterAuth
                    ? `/signup?redirect=${encodeURIComponent(redirectAfterAuth)}`
                    : '/signup'
                }
                className="text-primary underline-offset-4 hover:underline"
              >
                Sign up
              </Link>
            </p>
          </div>
        </form>
      </Form>
    </AuthLayout>
  )
}
